import { useEffect, useState } from "react";

// Body class set by a sticky note while it is being dragged / resized, so the
// controls underneath never pop up in the middle of a drag.
export const NOTE_DRAG_CLASS = "xz-note-dragging";

/**
 * True while the pointer is within `pad` px of the element (or the element has
 * keyboard focus inside it). Lets fixed controls sit BEHIND the sticky-note
 * layers by default and rise above them only when the user reaches for that
 * corner — without an invisible hot-zone that would steal clicks from notes.
 */
export default function useProximityRaise(ref, pad = 24) {
  const [raised, setRaised] = useState(false);

  useEffect(() => {
    let inside = false;
    let leaveTimer = 0;
    const onMove = (e) => {
      const el = ref.current;
      if (!el) return;
      if (document.body.classList.contains(NOTE_DRAG_CLASS)) return;
      const r = el.getBoundingClientRect();
      const hit =
        e.clientX >= r.left - pad &&
        e.clientX <= r.right + pad &&
        e.clientY >= r.top - pad &&
        e.clientY <= r.bottom + pad;
      if (hit && !inside) {
        inside = true;
        if (leaveTimer) {
          clearTimeout(leaveTimer);
          leaveTimer = 0;
        }
        setRaised(true);
      } else if (!hit && inside) {
        inside = false;
        // Small grace period so moving between the buttons never flickers.
        leaveTimer = setTimeout(() => {
          leaveTimer = 0;
          if (!el.contains(document.activeElement)) setRaised(false);
        }, 160);
      }
    };
    const onFocusIn = (e) => {
      const el = ref.current;
      if (el && el.contains(e.target)) setRaised(true);
    };
    const onFocusOut = (e) => {
      const el = ref.current;
      if (el && !el.contains(e.relatedTarget) && !inside) setRaised(false);
    };
    document.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      if (leaveTimer) clearTimeout(leaveTimer);
    };
  }, [ref, pad]);

  return raised;
}
